import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import budgetsScreenshot from "@/assets/budgets-targets-screenshot.png";
import auditScreenshot from "@/assets/demo-transactions.png";

interface Slide {
  label: string;
  headline: string;
  body: string;
  bullets: string[];
}

const slides: Slide[] = [
  {
    label: "1 / CASH POSITION",
    headline: "See your daily cash position—in real time",
    body: "Know where you stand today: cash in, cash out, fees, payouts, and true profitability—without waiting for month-end.",
    bullets: [
      "Live money-in / money-out view across accounts",
      "Daily profitability snapshot (today, week, month)",
      "Flags anomalies and overspending early"
    ]
  },
  {
    label: "2 / BUDGETS & TARGETS",
    headline: "Set budgets and targets. Track your pace.",
    body: "Define targets for revenue and spend, then monitor daily pacing so you can act before it's too late.",
    bullets: [
      "Budget by category / department",
      "Pace vs target tracking (daily + month-to-date)",
      "Simple variance explanations (what changed, why)"
    ]
  },
  {
    label: "3 / AUDIT",
    headline: "Audit every vendor and payment—end to end",
    body: "Go deeper than accounting software: CentralFlow groups payments by vendor/person, adds context, and makes every line item explainable.",
    bullets: [
      "Vendor profiles with contact details and notes",
      "All payments grouped under one name",
      "Attach invoices and flag suspicious patterns"
    ]
  }
];

// Slide visuals: video for slide 0, images for slides 1 and 2
const slideVisuals = [
  { type: "video", src: "/gop-demo.mov" },
  { type: "image", src: budgetsScreenshot },
  { type: "image", src: auditScreenshot }
];

export function FeatureSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(nextSlide, 7000);
    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);

  const slide = slides[currentSlide];
  const visual = slideVisuals[currentSlide];

  return (
    <section 
      className="py-14 sm:py-20 md:py-28 bg-white"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      <div className="container mx-auto px-5 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <span className="inline-block text-xs font-semibold tracking-[0.2em] text-primary uppercase mb-3 sm:mb-4">
            Everything you need
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
            Ready to take control?
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Three core workflows that replace spreadsheets and give you daily control.
          </p>
        </div>

        {/* Slider Content */}
        <div className="max-w-6xl mx-auto">
          {/* Light background container */}
          <div className="bg-gray-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-10 lg:p-12 border border-gray-200">
            <div className="grid lg:grid-cols-12 gap-5 sm:gap-8 lg:gap-10 items-center">
              
              {/* Left: Text Content */}
              <div className="lg:col-span-5 order-2 lg:order-1">
                {/* Label with fixed width for consistent alignment */}
                <div className="mb-2 sm:mb-3">
                  <span className="inline-flex items-center text-[10px] sm:text-xs font-semibold tracking-widest text-gray-600 uppercase font-mono">
                    <span className="w-4 text-right">{currentSlide + 1}</span>
                    <span className="mx-1.5">/</span>
                    <span>{slides[currentSlide].label.split(' / ')[1]}</span>
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
                  {slide.headline}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6 leading-relaxed">
                  {slide.body}
                </p>
                <ul className="space-y-2.5 sm:space-y-3">
                  {slide.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 sm:gap-3">
                      <div className="w-5 h-5 min-w-[20px] rounded-full bg-primary/15 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-primary" />
                      </div>
                      <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right: Visual (larger) */}
              <div className="lg:col-span-7 order-1 lg:order-2">
                <div className="relative">
                  <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl md:rounded-3xl p-2 sm:p-4 md:p-6 shadow-lg">
                    <div className="aspect-[16/10] bg-gray-50 rounded-lg sm:rounded-xl md:rounded-2xl relative overflow-hidden">
                      {visual.type === "video" ? (
                        <video
                          src={visual.src}
                          autoPlay
                          loop
                          muted
                          playsInline
                          preload="auto"
                          className="w-full h-full object-cover rounded-lg sm:rounded-xl md:rounded-2xl"
                        />
                      ) : (
                        <img
                          src={visual.src}
                          alt={slide.headline}
                          loading="eager"
                          decoding="async"
                          className="w-full h-full object-cover rounded-lg sm:rounded-xl md:rounded-2xl"
                        />
                      )}
                    </div>
                  </div>
                  {/* Decorative elements - hidden on mobile for cleaner look */}
                  <div className="hidden sm:block absolute -top-4 -right-4 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
                  <div className="hidden sm:block absolute -bottom-4 -left-4 w-32 h-32 bg-primary/10 rounded-full blur-2xl" />
                </div>
              </div>
            </div>
          </div>

          {/* Controls - Larger touch targets on mobile */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 mt-8 sm:mt-12">
            <Button
              variant="outline"
              size="icon"
              onClick={prevSlide}
              aria-label="Previous slide"
              className="rounded-full w-11 h-11 sm:w-10 sm:h-10 active:scale-95 transition-transform border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            {/* Dots - Larger touch targets for accessibility */}
            <div className="flex items-center gap-3 sm:gap-2">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`h-6 sm:h-6 rounded-full transition-all active:scale-90 flex items-center justify-center ${
                    idx === currentSlide 
                      ? "bg-primary w-10 sm:w-10" 
                      : "bg-gray-300 hover:bg-gray-400 w-6 sm:w-6"
                  }`}
                />
              ))}
            </div>
            
            <Button
              variant="outline"
              size="icon"
              onClick={nextSlide}
              aria-label="Next slide"
              className="rounded-full w-11 h-11 sm:w-10 sm:h-10 active:scale-95 transition-transform border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
